import {
  AmenitiesAdaAdaIcon,
  AmenitiesPetsPetFriendlyIcon,
  Button,
  Divider,
  EButtonColorVariant,
  ETextStyleVariant,
  LinkButton,
  NumberInput,
  Text,
  Toggle,
} from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useInitialGuests } from '@/hooks/useInitialInputs';
import { IHeaderSearchFilterGuests, setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { getHeaderSearchGuestsFilter } from '@/redux/selectors/globalHeader';
import { ESearchWidgetFilter } from '@/services/analytics/search/types';
import { trackEvent } from '@/services/track-event';

import { FilterContent, TFilterContentSharedProps } from './FilterContent';
import { EHomeSearchWidgetVariant } from './HomeSearchWidget';

type TGuestsFilterContentProps = TFilterContentSharedProps & {
  variant?: EHomeSearchWidgetVariant;
  // Optional override for the pet-friendly toggle's value + handler. When
  // provided, the toggle uses these instead of writing to
  // globalHeader.guests.pet_friendly. Used by the one-way search widget
  // to keep the toggle in sync with the pet-friendly pill, which writes
  // to URL filter[feature] rather than the guests redux slice.
  petFriendlyValue?: boolean;
  onPetFriendlyChange?: (value: boolean) => void;
  // Same pattern for ADA accessibility.
  accessibleValue?: boolean;
  onAccessibleChange?: (value: boolean) => void;
};

export const GuestsFilterContent = ({
  onBack,
  variant,
  petFriendlyValue,
  onPetFriendlyChange,
  accessibleValue,
  onAccessibleChange,
  ...props
}: TGuestsFilterContentProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const guests = useSelector(getHeaderSearchGuestsFilter);
  const { hasChanges, isInResetState, resetToInitial } = useInitialGuests(
    {
      adults: guests?.adults,
      children: guests?.children,
      infants: guests?.infants,
      pet_friendly: guests?.pet_friendly,
      accessible: guests?.accessible,
    },
    props.isOpened,
  );

  const changeGuestsSelection = (selection: IHeaderSearchFilterGuests) => {
    dispatch(
      setHeaderSearchFilter({
        guests: selection,
      }),
    );

    props.onTrackFilterToggled?.({ ...guests }, { ...selection }, ESearchWidgetFilter.Guests);

    Object.entries(selection)
      .filter(([key, value]) => value !== guests?.[key as keyof IHeaderSearchFilterGuests])
      .forEach(([key, value]) => {
        trackEvent({
          event: ['adults', 'children', 'infants'].includes(key)
            ? `search/filter/guests/sleeps[${key}]`
            : `search/filter/guests/${key}`,
          action: 'update',
          before: guests?.[key as keyof IHeaderSearchFilterGuests],
          after: value,
        });
      });
  };

  const getContent = () => {
    return (
      <div className="pb-6 lg:grid lg:grid-cols-6">
        <div className="lg:col-span-2">
          <Text variant={ETextStyleVariant.Label} className="mb-5 text-gray-500">
            {intl.formatMessage({
              defaultMessage: 'Guests',
              id: '1LkgyR',
              description: 'Home Search Widget > Guests Caption',
            })}
          </Text>

          <div className="flex flex-col lg:gap-4">
            <div className="flex items-center justify-between">
              <div className="pr-4">
                <Text variant={ETextStyleVariant.MediumRegular}>
                  {intl.formatMessage({
                    defaultMessage: 'Adults',
                    id: '11vOH4',
                    description: 'Home Search Widget > Adult Guests Selector Label',
                  })}
                </Text>

                <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-500">
                  {intl.formatMessage({
                    description: 'Home Search Widget > Adult Guests Selector Disclaimer',
                    defaultMessage: 'Ages 13 and up',
                    id: '8KaYQ5',
                  })}
                </Text>
              </div>

              <NumberInput
                ariaLabel={intl.formatMessage({
                  defaultMessage: 'Adults',
                  id: '11vOH4',
                  description: 'Home Search Widget > Adult Guests Selector Label',
                })}
                value={guests?.adults || 2}
                onChange={value => changeGuestsSelection({ ...guests, adults: value })}
                minValue={1}
                maxValue={10}
              />
            </div>

            <Divider className="my-4 lg:hidden" />

            <div className="flex items-center justify-between">
              <div className="pr-4">
                <Text variant={ETextStyleVariant.MediumRegular}>
                  {intl.formatMessage({
                    defaultMessage: 'Children',
                    id: 'GdHzLz',
                    description: 'Home Search Widget > Child Guests Selector Label',
                  })}
                </Text>

                <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-500">
                  {intl.formatMessage({
                    description: 'Home Search Widget > Child Guests Selector Disclaimer',
                    defaultMessage: 'Ages 12 or under',
                    id: 'rFSIog',
                  })}
                </Text>
              </div>

              <NumberInput
                ariaLabel={intl.formatMessage({
                  defaultMessage: 'Children',
                  id: 'GdHzLz',
                  description: 'Home Search Widget > Child Guests Selector Label',
                })}
                value={guests?.children || 0}
                onChange={value => changeGuestsSelection({ ...guests, children: value })}
                maxValue={10}
              />
            </div>

            <Divider className="my-4 lg:hidden" />
          </div>
        </div>

        <div className="lg:col-span-2 lg:col-start-4">
          <Text variant={ETextStyleVariant.Label} className="mb-5 text-gray-500">
            {intl.formatMessage({
              defaultMessage: 'Requirements',
              id: 'OHPsBi',
              description: 'Home Search Widget > Requirements Caption',
            })}
          </Text>

          <div className="flex flex-col lg:gap-5 gap-4">
            <div className="flex items-center justify-between">
              <Text variant={ETextStyleVariant.MediumRegular} className="flex items-center">
                <AmenitiesPetsPetFriendlyIcon
                  className="inline-flex mr-2 text-gray-700"
                  width={18}
                  height={18}
                />

                {intl.formatMessage({
                  defaultMessage: 'Pet-friendly',
                  id: 'Hh+Y9E',
                  description: 'Home Search Widget > Pet-friendly Selector Label',
                })}
              </Text>

              <Toggle
                ariaLabel={intl.formatMessage({
                  defaultMessage: 'Pet-friendly',
                  id: 'Hh+Y9E',
                  description: 'Home Search Widget > Pet-friendly Selector Label',
                })}
                isSelected={onPetFriendlyChange ? !!petFriendlyValue : !!guests?.pet_friendly}
                onChange={value =>
                  onPetFriendlyChange
                    ? onPetFriendlyChange(value)
                    : changeGuestsSelection({ ...guests, pet_friendly: value })
                }
              />
            </div>

            {variant !== EHomeSearchWidgetVariant.Campgrounds && (
              <div className="flex items-center justify-between">
                <Text variant={ETextStyleVariant.MediumRegular} className="flex items-center">
                  <AmenitiesAdaAdaIcon
                    className="inline-flex mr-2 text-gray-700"
                    width={18}
                    height={18}
                  />

                  {intl.formatMessage({
                    defaultMessage: 'ADA Accessible',
                    id: 'TYeibI',
                    description: 'Home Search Widget > ADA Accessible Selector Label',
                  })}
                </Text>

                <Toggle
                  ariaLabel={intl.formatMessage({
                    defaultMessage: 'ADA Accessible',
                    id: 'TYeibI',
                    description: 'Home Search Widget > ADA Accessible Selector Label',
                  })}
                  isSelected={onAccessibleChange ? !!accessibleValue : !!guests?.accessible}
                  onChange={value =>
                    onAccessibleChange
                      ? onAccessibleChange(value)
                      : changeGuestsSelection({ ...guests, accessible: value })
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <FilterContent
      onBack={() => {
        const initialValue = resetToInitial();
        changeGuestsSelection({
          adults: initialValue?.adults,
          children: initialValue?.children,
          infants: initialValue?.infants,
          pet_friendly: initialValue?.pet_friendly,
          accessible: initialValue?.accessible,
        });
        onBack?.();
      }}
      header={
        <>
          {intl.formatMessage({
            defaultMessage: 'Add guests',
            id: 'e+Z3VE',
            description: 'Home Search Widget > Guests Modal Title',
          })}
        </>
      }
      content={getContent()}
      footerContent={
        <FilterGuestFooter
          onClearGuests={() => {
            changeGuestsSelection({
              adults: 1,
              children: 0,
              infants: 0,
              pet_friendly: false,
              accessible: false,
            });
          }}
          onDone={() => {
            props.onDone?.();
          }}
          hasChanges={hasChanges}
          isInResetState={isInResetState}
        />
      }
      {...props}
    />
  );
};

type TFilterGuestFooterProps = {
  onClearGuests: () => void;
  onDone: () => void;
  hasChanges: boolean;
  isInResetState: boolean;
};

const FilterGuestFooter = ({
  onClearGuests,
  onDone,
  hasChanges,
  isInResetState,
}: TFilterGuestFooterProps) => {
  const intl = useIntl();
  const hasSingleAction = [onClearGuests, onDone].filter(x => x).length === 1;

  return (
    <div className="w-full">
      <div
        className={`flex items-center ${hasSingleAction ? 'justify-end' : 'justify-between'} p-4 lg:hidden`}>
        {onClearGuests && (
          <LinkButton
            label={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
            onClick={onClearGuests}
            disabled={isInResetState}
          />
        )}
        {onDone && (
          <Button
            variant={EButtonColorVariant.Primary}
            label={intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' })}
            onClick={onDone}
            disabled={!hasChanges}
          />
        )}
      </div>
    </div>
  );
};
